// A portal-based multi-select that ignores parent overflow and stays within viewport

import { useState, useEffect } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  size,
  autoUpdate,
  FloatingPortal,
} from '@floating-ui/react';
import GetClickOutside from './GetClickOutside';
import CheckBox from './CheckBox';
import FilterInput from './FilterInput';
import Spinner from './Spinner';
import { toast } from 'react-toastify';
import './MultiSelectInline.scss';

export default function MultiSelectInline({
  options,
  value,
  onChange,
  small,
  className,
  onClose,
  show,
  header,
  anchorRef,
  placement = 'bottom-start',
  search,
}) {
  const [spinner, setSpinner] = useState(false);
  const [newValue, setNewValue] = useState(value);
  const [query, setQuery] = useState('');

  const filteredOptions = search
    ? options.filter((opt) =>
        String(opt.label).toLowerCase().includes(query.toLowerCase()),
      )
    : options;

  useEffect(() => {
    setNewValue(value);
    if (!show) setQuery('');
  }, [value, show]);

  // Floating UI with fixed strategy to escape parent overflow
  const { refs, floatingStyles, update } = useFloating({
    placement,
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ['top-start', 'top-end'] }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight }) {
          const minPanelW = Math.max(320, Math.round(rects.reference.width));
          Object.assign(elements.floating.style, {
            minWidth: `${minPanelW}px`,
            maxWidth: 'min(92vw, 720px)',
          });

          const headerEl = elements.floating.querySelector('.header');
          const listEl = elements.floating.querySelector('.options');
          const okEl = elements.floating.querySelector('.ok-btn');

          const headerH = headerEl ? headerEl.offsetHeight : 0;
          const okH = okEl ? okEl.offsetHeight : 0;
          const verticalGaps = 32;

          const maxListH = Math.max(
            availableHeight - headerH - okH - verticalGaps,
            96,
          );

          if (listEl) {
            Object.assign(listEl.style, {
              maxHeight: `${Math.floor(maxListH)}px`,
              overflowY: 'auto',
            });
          }
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (anchorRef?.current) {
      refs.setReference(anchorRef.current);
      update?.();
    }
  }, [anchorRef, refs, update, show]);

  const filteredValues = filteredOptions.map((o) => o.value);

  return (
    <FloatingPortal>
      <GetClickOutside active={show} onClick={onClose}>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={
            'multi-select-inline' +
            (show ? ' show' : '') +
            (small ? ' small' : '') +
            (className ? ' ' + className : '')
          }
          role='dialog'
          aria-label={header}
          aria-hidden={!show}
        >
          {header && <p className='header'>{header}</p>}

          <div className='options'>
            {search && (
              <div className='search-row' onClick={(e) => e.stopPropagation()}>
                <FilterInput value={query} onChange={setQuery} small />
              </div>
            )}
            {search && query.trim() && filteredOptions.length === 0 ? (
              <p className='no-results'>No results found for your search</p>
            ) : (
              <CheckBox
                key='all'
                label='Select all'
                small={!!small}
                value={
                  filteredValues.length > 0 &&
                  filteredValues.every((v) => newValue?.includes(v))
                }
                onChange={(checked) => {
                  if (checked)
                    setNewValue([
                      ...new Set([...(newValue || []), ...filteredValues]),
                    ]);
                  else {
                    const fset = new Set(filteredValues);
                    setNewValue((newValue || []).filter((v) => !fset.has(v)));
                  }
                }}
                className='select-all'
              />
            )}
            {filteredOptions.map((option) => (
              <CheckBox
                key={String(option.value)}
                label={option.label}
                small={!!small}
                value={newValue?.includes(option.value)}
                onChange={(checked) => {
                  if (checked) {
                    setNewValue([
                      ...new Set([...(newValue || []), option.value]),
                    ]);
                  } else {
                    setNewValue(
                      (newValue || []).filter((v) => v !== option.value),
                    );
                  }
                }}
              />
            ))}
          </div>

          <button
            className={'ok-btn' + (small ? ' small' : '')}
            onClick={async () => {
              try {
                setSpinner(true);
                await onChange(newValue);
                onClose?.();
              } catch (error) {
                toast.error(error?.message || 'Something went wrong');
              } finally {
                setSpinner(false);
              }
            }}
          >
            Ok
          </button>

          <Spinner show={spinner} />
        </div>
      </GetClickOutside>
    </FloatingPortal>
  );
}
